import { useEffect, useState } from "react";
import { cachedGet } from "./api";
import { STAGES as FALLBACK_STAGES, STAGES_REQUIRING_TAG as FALLBACK_REQUIRING_TAG } from "./stages";

/**
 * Single source of truth for the stage list across the UI.
 * Pulls from /api/masters?type=stage&active_only=true so user-added stages
 * (e.g. "Bulk Email") show up everywhere without code changes.
 *
 * Falls back to the hardcoded constants only until the first fetch resolves,
 * so the UI never renders an empty <select> on initial paint.
 */
export function useStages() {
  const [stages, setStages] = useState(FALLBACK_STAGES);
  const [taggingRequired, setTaggingRequired] = useState(FALLBACK_REQUIRING_TAG);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await cachedGet("/masters?type=stage&active_only=true");
        if (cancelled || !Array.isArray(data)) return;
        const ordered = [...data].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        const values = ordered.map((s) => s.value);
        const reqTag = new Set(
          ordered.filter((s) => s?.extra?.tagging_required).map((s) => s.value)
        );
        if (values.length > 0) {
          setStages(values);
          setTaggingRequired(reqTag);
        }
      } catch {
        // keep fallback constants
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { stages, taggingRequired, loaded };
}
